import React, { useState } from "react";
import {
  Button,
  Tag,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  InputField,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@jem-open/jem-ui";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Users,
  Search,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import McmSegment from "../services/model/McmSegment";
import AffiliatedCompany from "../services/model/AffiliatedCompany";
import Segment from "../../segments/services/model/Segment";

type SegmentManagementPanelProps = {
  companies: AffiliatedCompany[];
  segments: McmSegment[];
  ownerCompanyName?: string;
  ownerSegments?: Segment[];
  isLoading: boolean;
  isDeleting?: boolean;
  onCreateSegment: (companyId: number) => void;
  onViewSegment: (segment: McmSegment) => void;
  onEditSegment: (segment: McmSegment) => void;
  onDeleteSegment: (segmentId: number, onSuccess: () => void) => void;
  onViewEmployees: (segment: McmSegment) => void;
  onGoBack: () => void;
};

const iconBtnClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-text-caption hover:text-greyscale-text-title hover:bg-greyscale-bg-subtle transition-colors";
const iconBtnDangerClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-text-caption hover:text-red-500 hover:bg-greyscale-bg-subtle transition-colors";
const iconBtnDisabledClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-border cursor-not-allowed";

const SegmentManagementPanel: React.FC<SegmentManagementPanelProps> = ({
  companies,
  segments,
  ownerCompanyName,
  ownerSegments,
  isLoading,
  isDeleting,
  onCreateSegment,
  onViewSegment,
  onEditSegment,
  onDeleteSegment,
  onViewEmployees,
  onGoBack,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCompanyIds, setExpandedCompanyIds] = useState<Set<number>>(new Set());
  const [deletingSegmentId, setDeletingSegmentId] = useState<number | null>(null);
  const [deletingSegmentName, setDeletingSegmentName] = useState("");

  const toggleCompany = (companyId: number): void => {
    setExpandedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  // Group segments by target_company.id; collect unassigned separately
  const segmentsByCompany = new Map<number, McmSegment[]>();
  const unassignedSegments: McmSegment[] = [];

  segments.forEach((seg) => {
    const companyId = seg.target_company?.id;
    if (companyId == null) {
      unassignedSegments.push(seg);
      return;
    }
    const list = segmentsByCompany.get(companyId);
    if (list) {
      list.push(seg);
    } else {
      segmentsByCompany.set(companyId, [seg]);
    }
  });

  // Filter by search query
  const matchesSearch = (seg: McmSegment): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      seg.name.toLowerCase().includes(q) ||
      (seg.target_company?.company_name?.toLowerCase().includes(q) ?? false)
    );
  };

  // Identify segments belonging to de-affiliated companies (target_company set but not in current companies list)
  const activeCompanyIds = new Set(companies.map((c) => c.target_employer.id));
  const deaffiliatedSegments: McmSegment[] = [];
  segmentsByCompany.forEach((segs, companyId) => {
    if (!activeCompanyIds.has(companyId)) {
      deaffiliatedSegments.push(...segs);
    }
  });

  // Determine which active companies have matching segments (or show all if no search)
  const companiesToShow = companies.filter((c) => {
    const companySegments = segmentsByCompany.get(c.target_employer.id) || [];
    if (!searchQuery) return true;
    return companySegments.some(matchesSearch) ||
      c.target_employer.company_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Filter owner segments by search query
  const matchesOwnerSearch = (seg: Segment): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      seg.name.toLowerCase().includes(q) ||
      (ownerCompanyName?.toLowerCase().includes(q) ?? false)
    );
  };

  const filteredOwnerSegments = (ownerSegments || []).filter(matchesOwnerSearch);
  const showOwnerRow = filteredOwnerSegments.length > 0;
  const isOwnerExpanded = expandedCompanyIds.has(-3);

  const filteredUnassigned = unassignedSegments.filter(matchesSearch);
  const filteredDeaffiliated = deaffiliatedSegments.filter(matchesSearch);
  const showUnassigned = filteredUnassigned.length > 0;
  const showDeaffiliated = filteredDeaffiliated.length > 0;
  const isUnassignedExpanded = expandedCompanyIds.has(-1);
  const isDeaffiliatedExpanded = expandedCompanyIds.has(-2);

  return (
    <div>
      <div className="flex items-center justify-between mb-lg">
        <div>
          <p className="text-base font-semibold text-greyscale-text-title">
            Manage Segments
          </p>
          <p className="text-sm text-greyscale-text-body mt-xxxs">
            Create and manage employee segments per company.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          <Button variant="outline" onClick={onGoBack}>
            Back to Messages
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-sm max-w-[300px]">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-greyscale-text-caption pointer-events-none z-10" />
        <InputField
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          placeholder="Search segments or companies..."
          className="w-full [&_input]:pl-9"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-greyscale-text-caption py-md">Loading segments...</p>
      ) : (
        <div className="flex flex-col gap-xs mb-xl">
          {/* Owner company segments (read-only) */}
          {showOwnerRow && (
            <div className="border border-greyscale-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-md py-sm bg-greyscale-bg-subtle">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCompany(-3)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleCompany(-3); }}
                  className="flex items-center gap-xs cursor-pointer"
                >
                  {isOwnerExpanded ? (
                    <ChevronDown size={16} className="text-greyscale-text-caption" />
                  ) : (
                    <ChevronRight size={16} className="text-greyscale-text-caption" />
                  )}
                  <span className="text-sm font-semibold text-greyscale-text-title">
                    {ownerCompanyName || "Your Company"}
                  </span>
                  <Tag variant="info">Owner</Tag>
                  <span className="text-xs text-greyscale-text-caption">
                    ({filteredOwnerSegments.length} segment{filteredOwnerSegments.length !== 1 ? "s" : ""})
                  </span>
                </div>
              </div>

              {isOwnerExpanded && (
                <div>
                  <div className="grid grid-cols-[1fr_140px_120px] gap-xs px-md py-xs bg-white border-t border-greyscale-border">
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Segment Name
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Employees
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Status
                    </span>
                  </div>

                  {filteredOwnerSegments.map((seg) => (
                    <div
                      key={`owner-${seg.id}`}
                      className="grid grid-cols-[1fr_140px_120px] gap-xs px-md py-xs border-t border-greyscale-border items-center"
                    >
                      <span className="text-sm text-greyscale-text-title">
                        {seg.name}
                      </span>
                      <span className="text-sm text-greyscale-text-body">
                        {seg.matched_employee_count ?? "\u2014"}
                      </span>
                      <div>
                        <Tag variant={seg.is_active ? "success" : "drafted"}>
                          {seg.is_active ? "Active" : "Inactive"}
                        </Tag>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {companiesToShow.map((company) => {
            const companyId = company.target_employer.id;
            const companySegments = (segmentsByCompany.get(companyId) || []).filter(matchesSearch);
            const isExpanded = expandedCompanyIds.has(companyId);

            return (
              <div
                key={companyId}
                className="border border-greyscale-border rounded-lg overflow-hidden"
              >
                {/* Company accordion header */}
                <div className="flex items-center justify-between px-md py-sm bg-greyscale-bg-subtle">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCompany(companyId)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleCompany(companyId); }}
                    className="flex items-center gap-xs cursor-pointer"
                  >
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-greyscale-text-caption" />
                    ) : (
                      <ChevronRight size={16} className="text-greyscale-text-caption" />
                    )}
                    <span className="text-sm font-semibold text-greyscale-text-title">
                      {company.target_employer.company_name}
                    </span>
                    <span className="text-xs text-greyscale-text-caption">
                      ({companySegments.length} segment{companySegments.length !== 1 ? "s" : ""})
                    </span>
                  </div>
                  <Button
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    onClick={() => onCreateSegment(companyId)}
                  >
                    New Segment
                  </Button>
                </div>

                {/* Expanded segment table */}
                {isExpanded && (
                  <div>
                    {/* Table header */}
                    {companySegments.length > 0 && (
                      <div className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs bg-white border-t border-greyscale-border">
                        <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                          Segment Name
                        </span>
                        <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                          Employees
                        </span>
                        <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                          Status
                        </span>
                        <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                          Actions
                        </span>
                      </div>
                    )}

                    {/* Segment rows */}
                    {companySegments.map((seg) => (
                      <div
                        key={seg.id}
                        className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs border-t border-greyscale-border items-center"
                      >
                        <span className="text-sm text-greyscale-text-title">
                          {seg.name}
                        </span>
                        <div className="flex items-center gap-xxs">
                          <span className="text-sm text-greyscale-text-body">
                            {seg.matched_employee_count ?? "\u2014"}
                          </span>
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <button
                                    aria-label="View employees"
                                    disabled={!seg.matched_employee_count}
                                    onClick={() => onViewEmployees(seg)}
                                    className={!seg.matched_employee_count ? iconBtnDisabledClass : iconBtnClass}
                                  >
                                    <Users size={14} />
                                  </button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent variant="dark">View Employees</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div>
                          <Tag variant={seg.in_use ? "success" : "drafted"}>
                            {seg.in_use ? "In Use" : "Not in Use"}
                          </Tag>
                        </div>
                        <div className="flex items-center justify-start gap-xxxs">
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  aria-label="View segment"
                                  onClick={() => onViewSegment(seg)}
                                  className={iconBtnClass}
                                >
                                  <Eye size={14} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent variant="dark">View</TooltipContent>
                            </Tooltip>
                            {!seg.in_use && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    aria-label="Edit segment"
                                    onClick={() => onEditSegment(seg)}
                                    className={iconBtnClass}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent variant="dark">Edit</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <button
                                    aria-label={seg.in_use ? "Cannot delete, segment in use" : "Delete segment"}
                                    disabled={seg.in_use}
                                    onClick={() => { setDeletingSegmentId(seg.id); setDeletingSegmentName(seg.name); }}
                                    className={seg.in_use ? iconBtnDisabledClass : iconBtnDangerClass}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent variant="dark">
                                {seg.in_use ? "Cannot delete \u2014 segment in use" : "Delete"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ))}

                    {companySegments.length === 0 && (
                      <div className="px-md py-sm border-t border-greyscale-border">
                        <p className="text-sm text-greyscale-text-caption">
                          No segments for this company yet.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned segments (no target company) */}
          {showUnassigned && (
            <div className="border border-greyscale-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-md py-sm bg-greyscale-bg-subtle">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCompany(-1)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleCompany(-1); }}
                  className="flex items-center gap-xs cursor-pointer"
                >
                  {isUnassignedExpanded ? (
                    <ChevronDown size={16} className="text-greyscale-text-caption" />
                  ) : (
                    <ChevronRight size={16} className="text-greyscale-text-caption" />
                  )}
                  <span className="text-sm font-semibold text-greyscale-text-title">
                    Unassigned
                  </span>
                  <span className="text-xs text-greyscale-text-caption">
                    ({filteredUnassigned.length} segment{filteredUnassigned.length !== 1 ? "s" : ""})
                  </span>
                </div>
              </div>

              {isUnassignedExpanded && (
                <div>
                  <div className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs bg-white border-t border-greyscale-border">
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Segment Name
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Employees
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Status
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Actions
                    </span>
                  </div>

                  {filteredUnassigned.map((seg) => (
                    <div
                      key={seg.id}
                      className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs border-t border-greyscale-border items-center"
                    >
                      <span className="text-sm text-greyscale-text-title">
                        {seg.name}
                      </span>
                      <div className="flex items-center gap-xxs">
                        <span className="text-sm text-greyscale-text-body">
                          {seg.matched_employee_count ?? "\u2014"}
                        </span>
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <button
                                  aria-label="View employees"
                                  disabled={!seg.matched_employee_count}
                                  onClick={() => onViewEmployees(seg)}
                                  className={!seg.matched_employee_count ? iconBtnDisabledClass : iconBtnClass}
                                >
                                  <Users size={14} />
                                </button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent variant="dark">View Employees</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div>
                        <Tag variant={seg.in_use ? "success" : "drafted"}>
                          {seg.in_use ? "In Use" : "Not in Use"}
                        </Tag>
                      </div>
                      <div className="flex items-center justify-start gap-xxxs">
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                aria-label="View segment"
                                onClick={() => onViewSegment(seg)}
                                className={iconBtnClass}
                              >
                                <Eye size={14} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent variant="dark">View</TooltipContent>
                          </Tooltip>
                          {!seg.in_use && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  aria-label="Edit segment"
                                  onClick={() => onEditSegment(seg)}
                                  className={iconBtnClass}
                                >
                                  <Pencil size={14} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent variant="dark">Edit</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <button
                                  aria-label={seg.in_use ? "Cannot delete, segment in use" : "Delete segment"}
                                  disabled={seg.in_use}
                                  onClick={() => { setDeletingSegmentId(seg.id); setDeletingSegmentName(seg.name); }}
                                  className={seg.in_use ? iconBtnDisabledClass : iconBtnDangerClass}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent variant="dark">
                              {seg.in_use ? "Cannot delete \u2014 segment in use" : "Delete"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* De-affiliated segments (company no longer in active list) */}
          {showDeaffiliated && (
            <div className="border border-greyscale-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-md py-sm bg-greyscale-bg-subtle">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCompany(-2)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleCompany(-2); }}
                  className="flex items-center gap-xs cursor-pointer"
                >
                  {isDeaffiliatedExpanded ? (
                    <ChevronDown size={16} className="text-greyscale-text-caption" />
                  ) : (
                    <ChevronRight size={16} className="text-greyscale-text-caption" />
                  )}
                  <span className="text-sm font-semibold text-greyscale-text-title">
                    De-affiliated
                  </span>
                  <span className="text-xs text-greyscale-text-caption">
                    ({filteredDeaffiliated.length} segment{filteredDeaffiliated.length !== 1 ? "s" : ""})
                  </span>
                </div>
              </div>

              {isDeaffiliatedExpanded && (
                <div>
                  <div className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs bg-white border-t border-greyscale-border">
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Segment Name
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Company
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Status
                    </span>
                    <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                      Actions
                    </span>
                  </div>

                  {filteredDeaffiliated.map((seg) => (
                    <div
                      key={seg.id}
                      className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs border-t border-greyscale-border items-center"
                    >
                      <span className="text-sm text-greyscale-text-title">
                        {seg.name}
                      </span>
                      <span className="text-sm text-greyscale-text-caption">
                        {seg.target_company?.company_name ?? "\u2014"}
                      </span>
                      <div>
                        <Tag variant={seg.in_use ? "success" : "drafted"}>
                          {seg.in_use ? "In Use" : "Not in Use"}
                        </Tag>
                      </div>
                      <div className="flex items-center justify-start gap-xxxs">
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                aria-label="View segment"
                                onClick={() => onViewSegment(seg)}
                                className={iconBtnClass}
                              >
                                <Eye size={14} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent variant="dark">View</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <button
                                  aria-label={seg.in_use ? "Cannot delete, segment in use" : "Delete segment"}
                                  disabled={seg.in_use}
                                  onClick={() => { setDeletingSegmentId(seg.id); setDeletingSegmentName(seg.name); }}
                                  className={seg.in_use ? iconBtnDisabledClass : iconBtnDangerClass}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent variant="dark">
                              {seg.in_use ? "Cannot delete \u2014 segment in use" : "Delete"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {companiesToShow.length === 0 && !showOwnerRow && !showUnassigned && !showDeaffiliated && (
            <div className="px-md py-sm">
              <p className="text-sm text-greyscale-text-caption">
                {searchQuery ? "No companies or segments matching your search." : "No affiliated companies found."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Delete segment confirmation dialog */}
      <Dialog
        open={!!deletingSegmentId}
        onOpenChange={(open) => { if (!open && !isDeleting) { setDeletingSegmentId(null); setDeletingSegmentName(""); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete segment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deletingSegmentName}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isDeleting}
              onClick={() => { setDeletingSegmentId(null); setDeletingSegmentName(""); }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={isDeleting}
              onClick={() => {
                if (deletingSegmentId) {
                  onDeleteSegment(deletingSegmentId, () => {
                    setDeletingSegmentId(null);
                    setDeletingSegmentName("");
                  });
                }
              }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentManagementPanel;
